import { storeToRefs } from 'pinia'
import { useCommonStore } from '@/stores/common.ts'
import { useEditorStore } from '@/stores/editor.ts'
import { nextTick } from 'vue'
import { useImage } from '@/views/editor/composable/image/useImage.ts'
import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'
import { deletePrefix, setDataType } from './useNoteUtils'
import { setTableData } from './useBlockCreate'
import { useCrop } from '@/composables/media/crop.ts'
import { restoreLatexElements } from '@/views/editor/composable/useLatexRestore.ts'

const sleepTime = 1000

let _pendingRetryTimer: ReturnType<typeof setTimeout> | null = null

export function cancelPendingRetry() {
	if (_pendingRetryTimer !== null) {
		clearTimeout(_pendingRetryTimer)
		_pendingRetryTimer = null
	}
}

/**
 * 소켓을 통한 블록 저장 (Debounce 적용)
 * @param targetType 대상 타입 (text, cell, function 등)
 * @param target 대상 요소
 */
export const socketSave = (targetType: string = 'text', target: any = null) => {
	const { socketTimeout } = storeToRefs(useCommonStore())
	if (socketTimeout.value !== null) {
		clearTimeout(socketTimeout.value)
	}

	socketTimeout.value = setTimeout(() => {
		saveBlockList(targetType, target)
	}, sleepTime)
}

/**
 * 즉시 소켓 저장 (Debounce 없음)
 * @param targetType 대상 타입
 * @param target 대상 요소
 * @param content 저장할 내용
 */
export const directSocketSave = async (targetType: string = 'text', target: any = null, content: string = ''): Promise<boolean> => {
	const { socketTimeout } = storeToRefs(useCommonStore())
	if (socketTimeout.value !== null) {
		clearTimeout(socketTimeout.value)
	}

	cancelPendingRetry()

	return await saveBlockList(targetType, target, content)
}

/**
 * 실제 블록 저장 로직 처리
 * @param targetType 대상 타입
 * @param target 대상 요소
 * @param content 저장할 내용
 */
const saveBlockList = async (targetType: string = 'text', target: any = null, content: string = ''): Promise<boolean> => {
	const { socket } = storeToRefs(useCommonStore())
	const { selector, noteUid, noteType, cellTarget, chartData } = storeToRefs(useEditorStore())
	await nextTick()

	if (document.getElementById('crop--container') || document.getElementById('crop--button__container')) {
		return false
	}

	useImage().deleteSizeElement()

	// blur 타입일 때는 selection 저장하지 않음 (무한 루프 방지)
	if (targetType !== 'blur') {
		new UseSelection().setChangeSelection()
	}

	// 타겟이 지정되지 않은 경우 현재 선택된 요소(셀 또는 블록)를 타겟으로 설정
	if (target === null) {
		if (targetType === 'cell' || targetType === 'function') {
			target = cellTarget.value
		} else {
			target = selector.value
		}
	}

	const originTarget = target

	if (!target) return false

	// TD 내부 요소인 경우 블록 단위로 타겟 변경
	if (target.nodeName === 'TD') {
		target = target.closest('.editor--block')
	}

	if (!target) return false

	// 블록이 아직 temp ID를 갖고 있으면 서버에서 real ID를 받을 때까지 대기
	if (target.id && !target.id.startsWith('prefix-')) {
		const { isLoadNote } = storeToRefs(useEditorStore())
		if (isLoadNote.value) {
			await new Promise<void>((resolve) => {
				const startTime = Date.now()
				const checkInterval = setInterval(() => {
					if (!isLoadNote.value || Date.now() - startTime > 5000) {
						clearInterval(checkInterval)
						resolve()
					}
				}, 50)
			})
		}
		// 대기 후에도 여전히 temp ID면 저장 스킵 (재시도를 위해 다시 스케줄)
		if (target.id && !target.id.startsWith('prefix-')) {
			cancelPendingRetry()
			_pendingRetryTimer = setTimeout(() => {
				_pendingRetryTimer = null
				socketSave(targetType, target)
			}, 500)
			return false
		}
	}

	await new Promise(requestAnimationFrame)

	// 빈 span 태그 정리
	const innerFontStyle = target.querySelectorAll('span')
	innerFontStyle.forEach((item: any) => {
		if (item.hasAttribute('data-latex') || item.closest('[data-latex]')) return
		if (item.id === 'calendar-span') return
		if (item.textContent === '') {
			item.remove()
		}
	})

	const type = target.dataset.type

	let didEmit = false

	// 텍스트 또는 이미지 블록 저장
	if (type === 'text' || type === 'image') {
		if (noteType.value === 'Project') {
			if (type === 'text' && target.querySelector('[data-latex]')) {
				restoreLatexElements(target)
			}
			const contentValue = content || target.innerHTML
			socket.value.modifyNoteBlock(noteUid.value, deletePrefix(target.id), contentValue, type)
			didEmit = true
		}
	} else if (type === 'chart') {
		// 차트 데이터 저장
		const targetUid = deletePrefix(target.id)
		let contentValue = ''
		chartData.value.forEach((item: any) => {
			if (item.blockUid === targetUid) {
				contentValue = item.chartDataList
			}
		})
		if (noteType.value === 'Project') {
			socket.value.modifyNoteBlock(noteUid.value, targetUid, contentValue, type)
			didEmit = true
		}
	} else if (type === 'defaultTable') {
		// 테이블 데이터 저장 로직 (modifyNoteBlock이 아닌 updateCell/createTableBlock 사용)
		const blockUid = deletePrefix(target.closest('.editor--block').id)
		const table = target.closest('.editor--block').querySelector('table')
		const tableUid = deletePrefix(table.id)

		if (blockUid.includes('temp') || tableUid.includes('temp')) {
			return false
		}

		const rows: any = []
		const rowList = [...table.rows]

		let allSize: any = {
			width: [],
			height: [],
		}

		// 테이블 열 너비 수집
		if (rowList.length > 0) {
			const numberLineCells = rowList[0].querySelectorAll('td.number--line')
			for (let i = 1; i < numberLineCells.length; i++) {
				if (numberLineCells[i]) {
					allSize.width.push(numberLineCells[i].getBoundingClientRect().width)
				}
			}
		}

		// 테이블 행 높이 수집 (style.height 우선, getBoundingClientRect는 border 포함하므로 보정)
		rowList.forEach((row: any, rowIndex: number) => {
			if (rowIndex !== 0) {
				const numberLine = row.cells[0] as HTMLElement
				const firstDataCell = row.querySelector('td:not(.number--line)') as HTMLTableCellElement
				if (firstDataCell) {
					// 병합 셀(rowSpan > 1)은 합산 높이를 가지므로 number-line 셀에서 개별 행 높이를 수집
					if (firstDataCell.rowSpan > 1) {
						if (numberLine) {
							allSize.height.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
						} else {
							allSize.height.push(40)
						}
					} else {
						const innerDiv = firstDataCell.querySelector('div') as HTMLElement
						if (innerDiv && innerDiv.style.height) {
							allSize.height.push(Math.round(parseFloat(innerDiv.style.height)))
						} else if (numberLine) {
							allSize.height.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
						} else {
							allSize.height.push(40)
						}
					}
				} else if (numberLine) {
					allSize.height.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
				} else {
					allSize.height.push(40)
				}
			}
		})

		// 테이블 셀 데이터 구성
		rowList.forEach((row: any, rowIndex: number) => {
			if (rowIndex !== 0) {
				const cellList = [...row.cells]
				rows[rowIndex - 1] = []
				cellList.forEach((cell: any, cellIndex: number) => {
					if (cellIndex !== 0) {
						const dataType: any = setDataType(cell, allSize)

						let cellContent: string
						if (cell.dataset.type === 'function' || cell.dataset.type === 'error') {
							cellContent = cell.querySelector('span')?.innerHTML ?? ''
						} else if (cell.dataset.type === 'image') {
							cellContent = cell.querySelector('img')?.outerHTML ?? ''
						} else {
							cellContent = cell.querySelector('div')?.innerHTML ?? ''
						}

						rows[rowIndex - 1].push({
							cellUid: deletePrefix(cell.id),
							colSpan: cell.colSpan,
							rowSpan: cell.rowSpan,
							content: cellContent,
							dataType: dataType,
						})
					}
				})
			}
		})

		const params = {
			noteUID: noteUid.value,
			blockUID: blockUid,
			new: false,
			content: {
				type: 'defaultTable',
				tableUID: tableUid,
				cell: rows,
			},
		}

		if (noteType.value === 'Project') {
			const saveTarget = originTarget.nodeName === 'TD' ? originTarget : cellTarget.value

			if (targetType === 'cell') {
				if (!saveTarget) return false
				if (deletePrefix(originTarget.id).includes('temp')) return false
				const dataType: any = setDataType(saveTarget)
				if (saveTarget.nodeName === 'TD') {
					const tdEl = saveTarget as HTMLTableCellElement
					dataType.colSpan = tdEl.colSpan
					dataType.rowSpan = tdEl.rowSpan
				}
				await new Promise(requestAnimationFrame)
				let updateCellContent: string
				if (originTarget.dataset.type === 'image') {
					updateCellContent = originTarget.querySelector('img')?.outerHTML ?? ''
				} else {
					updateCellContent = originTarget.querySelector('div').innerHTML
				}
				const paramsContent = {
					content: updateCellContent,
					dataType: dataType,
				}
				socket.value.updateCell(noteUid.value, blockUid, tableUid, deletePrefix(originTarget.id), paramsContent)
			} else if (targetType === 'function') {
				if (!saveTarget) return false
				if (deletePrefix(originTarget.id).includes('temp')) return false
				const dataType: any = setDataType(saveTarget)
				if (saveTarget.nodeName === 'TD') {
					const tdEl = saveTarget as HTMLTableCellElement
					dataType.colSpan = tdEl.colSpan
					dataType.rowSpan = tdEl.rowSpan
				}
				await new Promise(requestAnimationFrame)
				const paramsContent = {
					content: originTarget.querySelector('span').innerHTML,
					dataType: dataType,
				}
				socket.value.updateCell(noteUid.value, blockUid, tableUid, deletePrefix(originTarget.id), paramsContent)
			} else if (targetType === 'dropbox') {
				if (!saveTarget) return false
				if (deletePrefix(saveTarget.id).includes('temp')) return false
				const dataType: any = setDataType(saveTarget)
				if (saveTarget.nodeName === 'TD') {
					const tdEl = saveTarget as HTMLTableCellElement
					dataType.colSpan = tdEl.colSpan
					dataType.rowSpan = tdEl.rowSpan
				}
				await new Promise(requestAnimationFrame)
				const paramsContent = {
					content: '',
					dataType: dataType,
				}
				socket.value.updateCell(noteUid.value, blockUid, tableUid, deletePrefix(saveTarget.id), paramsContent)
			} else {
				socket.value.createTableBlock(params)
			}
			didEmit = true
		}

		useCreateTable().tableShapes(table)
		useCreateTable().widthSizeItem(table)
		useCreateTable().heightSizeItem(table)

		// blur 시에는 커서 복원 건너뛰기 (stale changeRange로 인한 포커스 점프 방지)
		if (targetType !== 'blur') {
			const { isUndoRedoInProgress, lastUndoRedoTime } = storeToRefs(useEditorStore())
			if (!isUndoRedoInProgress.value && Date.now() - lastUndoRedoTime.value >= 500) {
				const activeEl = document.activeElement
				const isInEditor = activeEl?.closest('#editor') || activeEl?.closest('.editor--container__content')
				const isInTableCell = activeEl?.closest('td') && !activeEl.closest('td')?.classList.contains('number--line')
				const savedBlock = target.closest('.editor--block') || target
				const activeBlock = activeEl?.closest('.editor--block')
				const isSavingActiveBlock = savedBlock && activeBlock && savedBlock === activeBlock
				if (!isInTableCell && isInEditor && isSavingActiveBlock) {
					new UseSelection().isCursorCheckPoint()
				}
			}
		}
	}

	// (주의) 여기서 히스토리를 저장하지 않는다.
	return didEmit
}

/**
 * 에디터 내 모든 테이블 블록을 소켓으로 저장
 * directSocketSave()는 현재 선택된 블록만 저장하므로,
 * 저장 버튼 클릭 시 다른 테이블 블록의 데이터가 유실되는 문제를 방지한다.
 */
export const saveAllTableBlocks = (): void => {
	const { socket } = storeToRefs(useCommonStore())
	const { noteUid, noteType, selector } = storeToRefs(useEditorStore())

	if (noteType.value !== 'Project') return

	const allTableBlocks = document.querySelectorAll('.editor--block[data-type="defaultTable"]')

	for (const block of Array.from(allTableBlocks)) {
		const htmlBlock = block as HTMLElement

		if (htmlBlock === selector.value) continue

		if (!htmlBlock.id || !htmlBlock.id.startsWith('prefix-')) continue

		const table = htmlBlock.querySelector('table') as HTMLTableElement
		if (!table) continue

		if (table.id.includes('temp')) continue

		const blockUid = deletePrefix(htmlBlock.id)
		const tableData = setTableData(table)

		const params = {
			noteUID: noteUid.value,
			blockUID: blockUid,
			new: false,
			content: {
				type: 'defaultTable',
				tableUID: tableData.tableUid || deletePrefix(table.id),
				cell: tableData.cell,
			},
		}

		socket.value.createTableBlock(params)
	}
}

/**
 * 현재 에디터의 모든 블록 데이터를 수집하여 반환
 * @returns 블록 데이터 객체
 */
export const setSaveData = () => {
	const { noteUid, chartData, noteType, isImageResize } = storeToRefs(useEditorStore())

	const blockItems = document.querySelectorAll('.editor--block')

	const blockData: any = {
		noteUid: noteUid.value,
		noteBlockList: [],
	}

	const tempNoteBlockList: any = []
	if (isImageResize.value) {
		const imageCropList = document.querySelectorAll('#crop--container')
		const imageCropButtonList = document.querySelectorAll('#crop--button__container')

		imageCropList.forEach((cropEl: any) => {
			const imageContainer = cropEl.closest('.image--container')
			if (imageContainer && imageContainer.dataset.crop === 'true') {
				imageContainer.style.width = imageContainer.dataset.width
				imageContainer.style.height = imageContainer.dataset.height
				const originImage = imageContainer.querySelector('img')
				if (originImage) {
					originImage.style.left = '-' + imageContainer.dataset.left
					originImage.style.top = '-' + imageContainer.dataset.top
				}
			}
			cropEl.remove()
		})
		imageCropButtonList.forEach((item: any) => {
			item.remove()
		})
	}

	blockItems.forEach((item: any, index: number) => {
		const data: any = {}
		data.blockUid = item.id.includes('temp') ? '' : deletePrefix(item.id)

		if (item.dataset.type === 'defaultTable') {
			try {
				data.content = setTableData(item.querySelector('table'))
			} catch (e) {
				data.content = ''
			}
		} else if (item.dataset.type === 'chart') {
			chartData.value.forEach((chart: any) => {
				if (chart.blockUid === deletePrefix(item.id)) {
					data.content = chart.chartDataList
				}
			})
		} else if (item.dataset.type === 'file') {
			const blockFileList: any = [...item.querySelectorAll('.file--content__list')]
			const blockUuid: any = []
			blockFileList.forEach((fileItem: any) => {
				blockUuid.push({ originName: fileItem.dataset.originName, uuidFileName: fileItem.dataset.file })
			})

			data.content = blockUuid
		} else {
			data.content = item.innerHTML
		}

		data.type = item.dataset.type
		data.sortOrder = index

		tempNoteBlockList.push(data)
	})

	blockData.noteBlockList = tempNoteBlockList

	return blockData
}
